import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProfileSchema } from "../schema/profile.schema";
import type { ProfileFormData, ProfileData } from "../types";
import { useUpdateProfile } from "../services/UpdateProfile";
import { useUpdateProfileImage } from "../services/UpdateImage";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { useLogout } from "@hooks/useLogout";

export const useProfileForm = (data: ProfileData | undefined, t: (key: string) => string) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [showPhoneChangeWarning, setShowPhoneChangeWarning] = useState<boolean>(false);
	const [pendingFormData, setPendingFormData] = useState<ProfileFormData | null>(null);
	const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
	const { mutate: updateProfileImage, isPending: isUploading } = useUpdateProfileImage();
	const { handleLogoutConfirm } = useLogout();

	const profileSchema = createProfileSchema(t);
	const form = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		defaultValues: {
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			phoneNumber: data?.phoneNumber || "",
			emailAddress: data?.emailAddress || "",
		},
	});

	// Reset form when data changes (e.g., after successful update)
	useEffect(() => {
		if (data) {
			form.reset({
				firstName: data.firstName || "",
				lastName: data.lastName || "",
				phoneNumber: data.phoneNumber || "",
				emailAddress: data.emailAddress || "",
			});
		}
	}, [data, form]);

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file) {
			updateProfileImage(file);
		}
	};

	const onSubmit = (formData: ProfileFormData): void => {

		const hasChanges =
			formData.firstName !== data?.firstName ||
			formData.lastName !== data?.lastName ||
			formData.emailAddress !== data?.emailAddress ||
			formData.phoneNumber !== data?.phoneNumber;


		if (!hasChanges) {
			setIsEditMode(false);
			return;
		}

		// Check if phone number has changed
		const phoneChanged = formData.phoneNumber !== data?.phoneNumber;
		
		if (phoneChanged) {
			// Show warning for phone number change
			setPendingFormData(formData);
			setShowPhoneChangeWarning(true);
			return;
		}

		updateProfile(formData, {
			onSuccess: () => {
				setIsEditMode(false);
			},
		});
	};

	const handleCancel = (): void => {
		setIsEditMode(false);
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handleEdit = (): void => {
		setIsEditMode(true);
		// Reset form to current data when entering edit mode
		form.reset({
			firstName: data?.firstName || "",
			lastName: data?.lastName || "",
			emailAddress: data?.emailAddress || "",
			phoneNumber: data?.phoneNumber || "",
		});
	};

	const handlePhoneChangeConfirm = (): void => {
		if (!pendingFormData) return;

		// Show notification about phone number change
		showNotification({
			title: t("psdaCommon.profile.phoneChangeWarningModal.modalTitle"),
			message: t("psdaCommon.profile.phoneChangeWarningModal.notificationMessage"),
			severity: SNACKBAR_SEVERITY.WARNING,
			autoHideDuration: 5000,
		});

		// Update profile with new phone number
		updateProfile(pendingFormData, {
			onSuccess: () => {
				setIsEditMode(false);
				setShowPhoneChangeWarning(false);
				setPendingFormData(null);
				
				// Logout user after successful phone number update
				setTimeout(() => {
					handleLogoutConfirm();
				}, 2000); // Give user time to see the success message
			},
			onError: error => {
				console.error("Profile update failed:", error);
				setShowPhoneChangeWarning(false);
				setPendingFormData(null);
			},
		});
	};

	const handlePhoneChangeCancel = (): void => {
		setShowPhoneChangeWarning(false);
		setPendingFormData(null);
	};

	return {
		isEditMode,
		isUpdating,
		isUploading,
		form,
		handleImageChange,
		onSubmit,
		handleCancel,
		handleEdit,
		showPhoneChangeWarning,
		handlePhoneChangeConfirm,
		handlePhoneChangeCancel,
	};
};
